import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Search, BookOpen, Briefcase, Code2, Palette, GraduationCap, Lock, Landmark } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/posthog";
import OakLogo from "@/components/OakLogo";

/**
 * /explore — Public index of every published preview path.
 *
 * Pinterest-style masonry layout (CSS columns, not JS-driven, so
 * it ships with zero extra JS weight). Each card surfaces:
 *   * Goal title (the SEO anchor)
 *   * Lens / category chip
 *   * "Free preview" tag — explicit gating disclosure for users
 *   * 5-line summary
 *
 * This page exists primarily for AI search engines: it's an internal
 * link hub that lets crawlers walk from oakaiapp.com → /explore →
 * every individual /p/{slug} preview. Without this hub, the long-tail
 * preview pages would only be reachable via direct sitemap entries —
 * which crawl far slower than internal-linked pages do.
 */
const CATEGORY_META = {
  Business: { Icon: Briefcase, accent: "#0044FF", glow: "rgba(0,68,255,.15)" },
  Tech: { Icon: Code2, accent: "#00FF9D", glow: "rgba(0,255,157,.15)" },
  Finance: { Icon: Landmark, accent: "#FFD86B", glow: "rgba(255,216,107,.15)" },
  Academic: { Icon: GraduationCap, accent: "#b9a8ff", glow: "rgba(185,168,255,.15)" },
  Other: { Icon: Palette, accent: "#ffb067", glow: "rgba(255,176,103,.15)" },
  // Legacy fallback — any older paths still tagged "Hobby" render with the same Palette icon as Other.
  Hobby: { Icon: Palette, accent: "#ffb067", glow: "rgba(255,176,103,.15)" },
};


export default function Explore() {
  const [data, setData] = useState({ items: [], categories: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    trackEvent("explore_viewed");
    let cancelled = false;
    api.get("/explore", { params: filter ? { category: filter } : {} })
      .then((res) => { if (!cancelled) { setData(res.data); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filter]);

  // Document head — same custom hook pattern as PublicPreview.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const prevTitle = document.title;
    document.title = filter
      ? `${filter} mastery paths · Oak AI Campus`
      : "Explore AI-guided mastery paths · Oak AI Campus";
    const desc = filter
      ? `Browse ${data.total || ""} AI-generated mastery paths in the ${filter} category — free 10% previews, full unlock with 6 free credits.`
      : "Browse 20+ AI-generated mastery paths across Business, Tech, Finance, Academic, and lifestyle categories. Free 10% previews on every path.";
    const created = [];
    const upsert = (sel, attrs) => {
      let el = document.head.querySelector(sel);
      let isNew = false;
      if (!el) {
        el = document.createElement(attrs.tag);
        document.head.appendChild(el);
        isNew = true;
      }
      Object.entries(attrs).forEach(([k, v]) => { if (k !== "tag" && v != null) el.setAttribute(k, v); });
      if (isNew) created.push(el);
    };
    upsert('meta[name="description"]', { tag: "meta", name: "description", content: desc });
    upsert('link[rel="canonical"]', { tag: "link", rel: "canonical", href: typeof window !== "undefined" ? `${window.location.origin}/explore${filter ? `?category=${filter}` : ""}` : "https://oakaiapp.com/explore" });
    upsert('meta[property="og:title"]', { tag: "meta", property: "og:title", content: document.title });
    upsert('meta[property="og:description"]', { tag: "meta", property: "og:description", content: desc });
    upsert('meta[property="og:image"]', { tag: "meta", property: "og:image", content: "/brand/oak-social-1200x630.png" });
    upsert('meta[name="twitter:card"]', { tag: "meta", name: "twitter:card", content: "summary_large_image" });

    // ItemList schema so search engines understand this is a structured
    // collection — boosts rich-result eligibility for "X mastery paths" queries.
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.dataset.explore = "1";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: document.title,
      description: desc,
      url: typeof window !== "undefined" ? window.location.href : "https://oakaiapp.com/explore",
      hasPart: (data.items || []).slice(0, 30).map((it, i) => ({
        "@type": "Course",
        position: i + 1,
        name: it.goal,
        description: it.summary,
        url: typeof window !== "undefined" ? `${window.location.origin}/p/${it.slug}` : `https://oakaiapp.com/p/${it.slug}`,
        isAccessibleForFree: false,
        provider: { "@type": "Organization", name: "Oak AI Campus" },
      })),
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
      ld.remove();
    };
  }, [filter, data]);

  const filteredItems = useMemo(() => {
    if (!q.trim()) return data.items || [];
    const needle = q.trim().toLowerCase();
    return (data.items || []).filter(
      (it) =>
        (it.goal || "").toLowerCase().includes(needle) ||
        (it.summary || "").toLowerCase().includes(needle) ||
        (it.first_step_title || "").toLowerCase().includes(needle)
    );
  }, [data.items, q]);

  const cats = data.categories || [];

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0B0E14]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="explore-brand">
            <OakLogo size={32} glow />
            <div className="font-display font-bold tracking-tight group-hover:text-[#00FF9D] transition">Oak AI Campus</div>
          </Link>
          <Link
            to="/login"
            className="px-4 py-2 rounded-full bg-[#0044FF] hover:bg-[#0035cc] text-white text-xs font-display font-semibold transition"
            data-testid="explore-cta-signin"
          >
            Sign in with Google
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF9D]/10 border border-[#00FF9D]/20 text-[#00FF9D] text-[11px] font-display font-semibold tracking-wide uppercase mb-5">
          <Sparkles size={11} /> Explore the Library
        </div>
        <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-3" data-testid="explore-title">
          {filter ? `${filter} mastery paths` : "Every mastery path, free to preview."}
        </h1>
        <p className="text-white/65 text-base md:text-lg max-w-2xl mb-8">
          {filter
            ? `${data.total || filteredItems.length} AI-guided whitepapers in the ${filter} category. Read the first 10% of any path — unlock the rest with your free 6 credits.`
            : "Browse our growing library of AI-guided mastery whitepapers. Free 10% previews on every path. No card required."}
        </p>

        {/* Search + filter chips */}
        <div className="flex flex-col gap-4 mb-8">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search paths..."
              className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-2.5 text-sm text-white/90 placeholder:text-white/35 focus:outline-none focus:border-[#0044FF]/50 transition"
              data-testid="explore-search"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="explore-filter-chips">
            <FilterChip label="All" active={!filter} onClick={() => setFilter(null)} count={data.total} />
            {cats.map((c) => (
              <FilterChip key={c} label={c} active={filter === c} onClick={() => setFilter(c)} accent={CATEGORY_META[c]?.accent} />
            ))}
          </div>
        </div>

        {/* Masonry grid — pure CSS columns, gracefully reflows on resize. */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="h-48 rounded-2xl bg-white/[0.03] border border-white/10 animate-pulse" />
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16 text-white/50">
            No paths match {q ? `"${q}"` : "this filter"} yet — check back soon.
          </div>
        ) : (
          <div className="columns-1 md:columns-2 lg:columns-3 gap-5 [column-fill:_balance]" data-testid="explore-masonry">
            {filteredItems.map((item, idx) => (
              <PathCard key={item.slug} item={item} idx={idx} />
            ))}
          </div>
        )}

        <ExploreFooter />
      </main>
    </div>
  );
}


function FilterChip({ label, active, onClick, count, accent }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`explore-chip-${label.toLowerCase()}`}
      className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-display font-semibold transition border ${
        active
          ? "bg-white text-[#0B0E14] border-white"
          : "bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08] hover:text-white"
      }`}
      style={!active && accent ? { boxShadow: `inset 0 0 0 1px ${accent}30` } : undefined}
    >
      {label}
      {count != null && active && <span className="text-[10px] opacity-60 ml-1">{count}</span>}
    </button>
  );
}


function PathCard({ item, idx }) {
  const meta = CATEGORY_META[item.category] || CATEGORY_META.Other;
  const Icon = meta.Icon;
  const tierLabel = (item.tier || "intermediate").toUpperCase();
  // Stagger card heights slightly to give the masonry visual rhythm.
  // Pure CSS columns will pack them naturally — we add subtle padding
  // variance so adjacent cards don't all line up at the same height.
  const padTop = idx % 3 === 0 ? "pt-6" : idx % 3 === 1 ? "pt-7" : "pt-5";

  return (
    <Link
      to={`/p/${item.slug}`}
      className={`block break-inside-avoid mb-5 group rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] hover:border-white/25 hover:bg-white/[0.06] transition p-6 ${padTop} relative overflow-hidden`}
      data-testid={`explore-card-${item.slug}`}
      onClick={() => trackEvent("explore_card_clicked", { slug: item.slug, category: item.category })}
    >
      {/* Glow */}
      <div
        className="absolute -top-12 -right-10 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition pointer-events-none"
        style={{ background: `radial-gradient(circle, ${meta.glow} 0%, transparent 70%)` }}
      />

      <div className="flex items-center justify-between mb-4 relative">
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-display font-semibold tracking-wider uppercase border"
          style={{
            color: meta.accent,
            borderColor: `${meta.accent}40`,
            background: `${meta.accent}14`,
          }}
        >
          <Icon size={11} /> {item.category || "Other"}
        </div>
        <span className="text-[10px] font-display font-semibold tracking-wider uppercase text-[#00FF9D]/85">
          Free preview
        </span>
      </div>

      <h2 className="font-display text-lg md:text-xl font-semibold leading-snug tracking-tight mb-3 group-hover:text-white transition">
        {item.goal}
      </h2>
      {item.summary && (
        <p className="text-white/55 text-sm leading-relaxed mb-4 line-clamp-4">{item.summary}</p>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-white/40">
        <span className="font-medium">{item.step_count} steps</span>
        <span className="text-white/15">·</span>
        <span className="font-medium" style={{ color: meta.accent }}>{tierLabel}</span>
        <span className="text-white/15">·</span>
        <span className="font-medium uppercase">{item.language}</span>
      </div>

      <div className="mt-5 flex items-center justify-between text-xs text-white/45 group-hover:text-white/85 transition">
        <span className="inline-flex items-center gap-1.5">
          <Lock size={11} /> 6 credits to unlock
        </span>
        <span className="inline-flex items-center gap-1 font-display font-semibold">
          Read preview <ArrowRight size={12} className="group-hover:translate-x-0.5 transition" />
        </span>
      </div>
    </Link>
  );
}


function ExploreFooter() {
  return (
    <footer className="mt-20 pt-8 border-t border-white/5">
      <div className="rounded-3xl border border-[#00FF9D]/25 bg-gradient-to-br from-[#0044FF]/10 via-[#0B0E14]/40 to-[#00FF9D]/10 p-7 md:p-10 text-center backdrop-blur-xl">
        <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-3">
          Build your own mastery path
        </h3>
        <p className="text-white/65 max-w-xl mx-auto mb-6 text-sm md:text-base">
          Tell our AI Counselor any goal — get a 5-step path tailored to you. Every path includes a Live Doubt Solver tutor and an adaptive exam.
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-[#0B0E14] font-display font-bold text-sm md:text-base hover:scale-[1.03] transition"
          data-testid="explore-cta-build"
        >
          Sign in with Google · 6 free credits <ArrowRight size={16} />
        </Link>
      </div>
      <div className="mt-10 text-xs text-white/40 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
        <Link to="/" className="hover:text-white/80 transition">Oak AI Campus</Link>
        <span>·</span>
        <a href="/api/og/sitemap.xml" className="hover:text-white/80 transition">Sitemap</a>
        <span>·</span>
        <Link to="/privacy" className="hover:text-white/80 transition">Privacy</Link>
      </div>
    </footer>
  );
}
